import './feedback.css';
import { t, tText } from './i18n';

/**
 * 건의하기: a one-way message to admins. Centered modal, single textarea, send
 * — there's no reply and nothing to read back, so there's no list or thread
 * view, just compose-and-confirm.
 */
export interface FeedbackPanelCallbacks {
  onSend(text: string): void;
  onClose(): void;
}

function el<K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  return e;
}

export class FeedbackPanel {
  private readonly overlay = el('div', 'feedback-overlay ui-scrim');
  private readonly modal = el('div', 'feedback-modal interactive');
  private readonly textarea = el('textarea', 'feedback-textarea') as HTMLTextAreaElement;
  private readonly sendBtn = el('button', 'review-action feedback-send');
  private readonly sentNote = el('div', 'feedback-sent');
  private readonly sentIcon = el('div', 'feedback-sent-icon');
  private readonly sentText = el('div', 'feedback-sent-text');

  constructor(root: HTMLElement, private cb: FeedbackPanelCallbacks) {
    const header = el('div', 'review-header');
    const title = el('div', 'review-title');
    tText(title, 'feedback.title');
    const close = el('button', 'review-close');
    tText(close, 'mainmenu.back');
    close.onclick = () => {
      this.hide();
      this.cb.onClose();
    };
    header.append(title, close);

    this.textarea.rows = 6;
    this.textarea.maxLength = 2000;

    tText(this.sendBtn, 'feedback.send');
    this.sendBtn.onclick = () => {
      const text = this.textarea.value.trim();
      if (!text) return;
      this.sendBtn.setAttribute('disabled', 'true');
      this.cb.onSend(text);
    };

    this.sentIcon.textContent = '📬';
    tText(this.sentText, 'feedback.sent');
    this.sentNote.append(this.sentIcon, this.sentText);
    this.sentNote.style.display = 'none';

    this.modal.append(header, this.textarea, this.sendBtn, this.sentNote);
    this.overlay.append(this.modal);
    this.overlay.addEventListener('pointerdown', (e) => {
      if (e.target === this.overlay) {
        this.hide();
        this.cb.onClose();
      }
    });
    root.appendChild(this.overlay);
  }

  get isOpen(): boolean {
    return this.overlay.classList.contains('open');
  }

  /** Show the panel, ready for a fresh message. */
  open(): void {
    this.textarea.value = '';
    this.textarea.placeholder = t('feedback.messagePlaceholder');
    this.textarea.style.display = '';
    this.sendBtn.style.display = '';
    this.sendBtn.removeAttribute('disabled');
    this.sentNote.style.display = 'none';
    this.overlay.classList.add('open');
    this.textarea.focus();
  }

  hide(): void {
    this.overlay.classList.remove('open');
  }

  /** The report went through — swap the form for a brief thank-you (with a
   *  little mailbox animation), then auto-close. */
  onSent(): void {
    this.textarea.style.display = 'none';
    this.sendBtn.style.display = 'none';
    this.sentNote.style.display = '';
    setTimeout(() => {
      this.hide();
      this.cb.onClose();
    }, 3000);
  }

  /** The submission failed (rare) — let the user try again. */
  onError(): void {
    this.sendBtn.removeAttribute('disabled');
  }
}
